/**
 * Doserne Kurs — Course viewer.
 * Sequential lesson navigation → quiz → results/certificate.
 */
import React, { useEffect, useMemo, useState } from "react";
import { Link, Navigate, useParams } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import {
  Loader2, ArrowLeft, ArrowRight, CheckCircle2, XCircle, Lock, Award,
  PlayCircle, ClipboardList, Users, Shield, ChevronDown, Sparkles, Star,
  TrendingUp, Calendar, Layers, MapPin,
  AlertTriangle, Ban, Megaphone, OctagonAlert, Info,
  Download, Trophy, RotateCcw,
} from "lucide-react";
import { useDoserneAuth } from "./doserneAuth";
import { useLang, pick } from "./i18n";
import DoserneShell from "./DoserneShell";
import { PenSquare, FileSignature } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

/* pickHero — given a doc that may carry `hero_image` plus an admin
 * `hero_image_override` map (default + per-language), return the image URL
 * that should actually be shown to the user in `lang`. */
function pickHero(doc, lang) {
  if (!doc) return "";
  const ov = doc.hero_image_override || {};
  const langKey = `hero_image_${lang}`;
  return ov[langKey] || ov.hero_image || doc.hero_image || "";
}

/* ============================================================
   CoursePassedBanner — polls /completion-status and surfaces the
   "sign to receive certificate" CTA or the "certificate issued"
   card depending on state. Rendered above the lesson list.
============================================================ */
function CoursePassedBanner({ course, lang, t, passedCount, total }) {
  const { authHeader } = useDoserneAuth();
  const [status, setStatus] = useState(null);
  const openCertPdf = async (certId) => {
    if (!certId) return;
    try {
      const { data } = await axios.get(`${API}/doserne/certificates/${certId}/pdf`, {
        ...authHeader(), responseType: "blob",
      });
      const url = window.URL.createObjectURL(new Blob([data], { type: "application/pdf" }));
      window.open(url, "_blank");
      setTimeout(() => window.URL.revokeObjectURL(url), 60000);
    } catch { /* ignore */ }
  };
  useEffect(() => {
    if (!course) return;
    if (passedCount < total || total === 0) { setStatus(null); return; }
    let alive = true;
    axios.get(`${API}/doserne/kurs/${course.code}/completion-status`, authHeader())
      .then(({ data }) => { if (alive) setStatus(data); })
      .catch(() => { if (alive) setStatus(null); });
    return () => { alive = false; };
  }, [course, passedCount, total, authHeader]);

  if (!status) return null;

  const labels = {
    no: { needSign: "Alle leksjoner er bestått!", needSignSub: "Signer den siste bekreftelsen for å motta kursbeviset ditt.", signBtn: "Signer og motta kursbeviset", completed: "Kurset er fullført", completedSub: "Kursbeviset ditt er utstedt.", viewCert: "Vis kursbevis (PDF)", viewAck: "Vis signert bekreftelse" },
    ro: { needSign: "Toate lecțiile sunt promovate!", needSignSub: "Semnează confirmarea finală pentru a primi certificatul.", signBtn: "Semnează și primește certificatul", completed: "Curs finalizat", completedSub: "Certificatul tău a fost emis.", viewCert: "Vezi certificatul (PDF)", viewAck: "Vezi confirmarea semnată" },
    en: { needSign: "All lessons passed!", needSignSub: "Sign the final acknowledgement to receive your certificate.", signBtn: "Sign and receive certificate", completed: "Course completed", completedSub: "Your certificate has been issued.", viewCert: "View certificate (PDF)", viewAck: "View signed acknowledgement" },
  };
  const L = labels[lang] || labels.no;

  if (status.state === "passed_signature_required") {
    return (
      <div className="mb-4 rounded-3xl bg-gradient-to-br from-slate-900 to-slate-800 text-white p-5 sm:p-6 shadow-lg" data-testid="ack-cta-banner">
        <div className="text-xs font-bold uppercase tracking-[0.3em] text-orange-300 mb-2">{L.needSign}</div>
        <div className="text-lg sm:text-xl font-black mb-3">{L.needSignSub}</div>
        <Link
          to={`/doserne/kurs/${course.code}/sign`}
          className="inline-flex items-center gap-2 px-5 py-3 rounded-2xl bg-orange-500 hover:bg-orange-600 active:bg-orange-700 font-black text-white text-sm shadow"
          data-testid="ack-cta-btn"
        >
          <PenSquare className="w-4 h-4" /> {L.signBtn}
        </Link>
      </div>
    );
  }

  if (status.state === "completed" && status.certificate) {
    return (
      <div className="mb-4 rounded-3xl bg-white border border-emerald-200 p-5 sm:p-6 shadow-sm" data-testid="completed-banner">
        <div className="flex flex-col sm:flex-row sm:items-center gap-3 sm:gap-4">
          <div className="w-12 h-12 rounded-2xl bg-emerald-500 text-white flex items-center justify-center flex-none"><Award className="w-6 h-6" /></div>
          <div className="flex-1">
            <div className="text-xs font-bold uppercase tracking-[0.3em] text-emerald-700 mb-1">{L.completed}</div>
            <div className="text-slate-900 font-black">{L.completedSub}</div>
            <div className="text-xs font-mono text-slate-500 mt-0.5">{status.certificate.cert_number}</div>
          </div>
          <div className="flex flex-col gap-2 sm:min-w-[200px]">
            <button
              type="button" onClick={() => openCertPdf(status.certificate.id)}
              className="inline-flex items-center justify-center gap-2 px-4 py-2.5 rounded-xl bg-slate-900 text-white text-sm font-bold hover:bg-slate-800"
              data-testid="completed-view-cert"
            >
              <Award className="w-4 h-4" /> {L.viewCert}
            </button>
            <Link
              to={`/doserne/kurs/${course.code}/sign`}
              className="inline-flex items-center justify-center gap-2 px-4 py-2.5 rounded-xl bg-slate-100 text-slate-900 text-sm font-bold hover:bg-slate-200"
            >
              <FileSignature className="w-4 h-4" /> {L.viewAck}
            </Link>
          </div>
        </div>
      </div>
    );
  }

  return null;
}

/* ============================================================
   Lesson renderers — every text field flows through pick(field, lang).
============================================================ */
function TakeawayBox({ lesson, lang, t }) {
  const list = Array.isArray(lesson.takeaways) && lesson.takeaways.length
    ? lesson.takeaways
    : (lesson.takeaway ? [{ body: lesson.takeaway }] : []);
  if (!list.length) return null;
  return (
    <>
      {list.map((box, i) => (
        <div key={i} className="mt-5 bg-cyan-50 border-l-4 border-cyan-500 rounded-r-xl p-4" data-testid={`lesson-takeaway-${i}`}>
          <div className="text-[10px] uppercase font-bold tracking-widest text-cyan-700 mb-1 inline-flex items-center gap-1">
            <Star className="w-3 h-3" /> {box.title ? pick(box.title, lang) : t("kurs.note")}
          </div>
          <div className="text-sm font-bold text-slate-900">{pick(box.body, lang)}</div>
        </div>
      ))}
    </>
  );
}

function LessonWelcome({ lesson, lang, t }) {
  return (
    <div>
      <div className="text-6xl mb-4">👋</div>
      <p className="text-base sm:text-lg text-slate-700 whitespace-pre-wrap leading-relaxed">{pick(lesson.body, lang)}</p>
      {lesson.video_caption && (
        <div className="mt-6 bg-slate-50 border border-dashed border-slate-300 rounded-xl p-4 text-center">
          <PlayCircle className="w-8 h-8 text-slate-400 mx-auto mb-2" />
          <p className="text-xs text-slate-500">{pick(lesson.video_caption, lang)}</p>
        </div>
      )}
      <TakeawayBox lesson={lesson} lang={lang} t={t} />
    </div>
  );
}

/* Icon map for the intro_hero principles cards.
   Keep the map small and explicit — new principles only need a matching key. */
const PRINCIPLE_ICONS = {
  shield: Shield,
  award: Award,
  users: Users,
  "trending-up": TrendingUp,
};
const PRINCIPLE_COLORS = [
  { bg: "bg-cyan-50",    icon: "text-cyan-600",    ring: "ring-cyan-200"    },
  { bg: "bg-amber-50",   icon: "text-amber-600",   ring: "ring-amber-200"   },
  { bg: "bg-emerald-50", icon: "text-emerald-600", ring: "ring-emerald-200" },
  { bg: "bg-indigo-50",  icon: "text-indigo-600",  ring: "ring-indigo-200"  },
];

// Separate icon set for the small factual highlight strip (Established / m² / offices).
const HIGHLIGHT_ICONS = {
  calendar: Calendar,
  layers: Layers,
  "map-pin": MapPin,
};

function LessonIntroHero({ lesson, lang }) {
  const paragraphs = lesson.paragraphs || [];
  const principles = lesson.principles || [];
  const closing = lesson.closing_paragraphs || [];

  return (
    <div className="max-w-[68ch] mx-auto">
      {/* Hero image */}
      {pickHero(lesson, lang) && (
        <div className="relative rounded-2xl overflow-hidden mb-8 aspect-[16/6] bg-slate-100">
          <img
            src={pickHero(lesson, lang)}
            alt={pick(lesson.hero_alt, lang) || ""}
            className="w-full h-full object-cover"
            data-testid="intro-hero-image"
          />
          <div className="absolute inset-0 bg-gradient-to-t from-black/30 to-transparent" />
        </div>
      )}

      {/* Intro line — larger, warm */}
      {lesson.intro && (
        <p className="text-xl sm:text-2xl font-bold text-slate-900 mb-6 leading-snug" data-testid="intro-hero-intro">
          {pick(lesson.intro, lang)}
        </p>
      )}

      {/* Opening paragraphs */}
      <div className="space-y-4 mb-8">
        {paragraphs.map((p, i) => (
          <p key={i} className="text-[17px] text-slate-700 leading-relaxed">{pick(p, lang)}</p>
        ))}
      </div>

      {/* Fact highlights strip — small horizontal row that anchors the numbers */}
      {Array.isArray(lesson.highlights) && lesson.highlights.length > 0 && (
        <div
          className="grid grid-cols-1 sm:grid-cols-3 gap-3 mb-8"
          data-testid="intro-hero-highlights"
        >
          {lesson.highlights.map((h, i) => {
            const Icon = HIGHLIGHT_ICONS[h.icon] || Sparkles;
            return (
              <div
                key={i}
                className="rounded-2xl bg-slate-900 p-4 flex items-center gap-3"
                style={{ color: "#fff" }}
                data-testid={`highlight-card-${i}`}
              >
                <div className="w-10 h-10 rounded-xl bg-cyan-500/15 flex items-center justify-center text-cyan-300 flex-shrink-0">
                  <Icon className="w-5 h-5" />
                </div>
                <div className="text-sm sm:text-[15px] font-bold leading-snug">
                  {pick(h.label, lang)}
                </div>
              </div>
            );
          })}
        </div>
      )}

      {/* Principles intro + cards */}
      {principles.length > 0 && (
        <div className="mb-8">
          {lesson.principles_intro && (
            <p className="text-[17px] text-slate-700 leading-relaxed mb-4">{pick(lesson.principles_intro, lang)}</p>
          )}
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-3" data-testid="intro-hero-principles">
            {principles.map((p, i) => {
              const Icon = PRINCIPLE_ICONS[p.icon] || Shield;
              const c = PRINCIPLE_COLORS[i % PRINCIPLE_COLORS.length];
              return (
                <div
                  key={i}
                  className={`${c.bg} rounded-2xl p-4 ring-1 ${c.ring} h-full`}
                  data-testid={`principle-card-${i}`}
                >
                  <div className={`w-10 h-10 rounded-xl bg-white flex items-center justify-center mb-3 ${c.icon}`}>
                    <Icon className="w-5 h-5" />
                  </div>
                  <div className="text-base font-black text-slate-900 leading-tight">{pick(p.title, lang)}</div>
                  <div className="text-sm text-slate-600 mt-1 leading-snug">{pick(p.subtitle, lang)}</div>
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* Closing paragraphs */}
      <div className="space-y-4 mb-8">
        {closing.map((p, i) => (
          <p key={i} className="text-[17px] text-slate-700 leading-relaxed">{pick(p, lang)}</p>
        ))}
      </div>

      {/* Signoff — highlighted panel */}
      {lesson.signoff && (
        <div
          className="rounded-2xl overflow-hidden border border-slate-900/10 bg-gradient-to-br from-slate-900 via-slate-900 to-cyan-900 p-6 flex items-center gap-4"
          data-testid="intro-hero-signoff"
        >
          <div className="w-12 h-12 rounded-xl bg-white/10 backdrop-blur flex items-center justify-center ring-1 ring-white/20 flex-shrink-0">
            <Users className="w-6 h-6 text-cyan-300" />
          </div>
          <div className="flex-1">
            <div className="text-[10px] uppercase tracking-[0.3em] text-cyan-300 font-bold">Doserne</div>
            <div className="text-xl sm:text-2xl font-black tracking-tight" style={{ color: "#fff" }}>
              {pick(lesson.signoff, lang).toUpperCase()}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

/* ============================================================
   Safety lesson (HSE course) — rich composite renderer.
   Sections:
     - hero image + intro (large, warm)
     - opening paragraphs
     - responsibility bullet list ("everyone must:")
     - emphasis quote (highlighted cyan panel)
     - section body (short paragraph)
     - "Before work starts" checklist
     - 3 key action cards (Identify / Stop / Report)
     - Stop Work Authority block (title + body + bullet list)
     - After stopping work bullet list
     - red warning line
     - source note + disclaimer (small print)
============================================================ */
const SAFETY_ICONS = {
  "alert-triangle": AlertTriangle,
  ban: Ban,
  megaphone: Megaphone,
};
const SAFETY_COLORS = [
  { bg: "bg-amber-50",  ring: "ring-amber-200",  iconBg: "bg-amber-500",  chip: "text-amber-700" },
  { bg: "bg-red-50",    ring: "ring-red-200",    iconBg: "bg-red-500",    chip: "text-red-700"   },
  { bg: "bg-emerald-50",ring: "ring-emerald-200",iconBg: "bg-emerald-500",chip: "text-emerald-700" },
];

function BulletList({ items, lang, tone = "slate" }) {
  const dot = tone === "red" ? "bg-red-500"
            : tone === "amber" ? "bg-amber-500"
            : "bg-cyan-500";
  return (
    <ul className="space-y-3">
      {(items || []).map((it, i) => (
        <li key={i} className="flex items-start gap-3">
          <span className={`mt-2.5 w-1.5 h-1.5 rounded-full ${dot} flex-shrink-0`} />
          <span className="text-[17px] text-slate-800 leading-relaxed">{pick(it, lang)}</span>
        </li>
      ))}
    </ul>
  );
}

function LessonSafety({ lesson, lang }) {
  const paragraphs = lesson.paragraphs || [];
  const respItems = lesson.responsibility_items || [];
  const beforeItems = lesson.before_work_items || [];
  const stopItems = lesson.stop_work_items || [];
  const afterItems = lesson.after_stop_items || [];
  const keyCards = lesson.key_cards || [];
  const sections = lesson.numbered_sections || [];

  return (
    <div className="max-w-[68ch] mx-auto">
      {/* Hero image */}
      {pickHero(lesson, lang) && (
        <div className="relative rounded-2xl overflow-hidden mb-8 aspect-[16/6] bg-slate-100">
          <img
            src={pickHero(lesson, lang)}
            alt={pick(lesson.hero_alt, lang) || ""}
            className="w-full h-full object-cover"
            data-testid="safety-hero-image"
          />
          <div className="absolute inset-0 bg-gradient-to-t from-black/40 to-transparent" />
        </div>
      )}

      {/* Intro */}
      {lesson.intro && (
        <p className="text-xl sm:text-2xl font-bold text-slate-900 mb-6 leading-snug">
          {pick(lesson.intro, lang)}
        </p>
      )}

      {/* Opening paragraphs */}
      <div className="space-y-4 mb-8">
        {paragraphs.map((p, i) => (
          <p key={i} className="text-[18px] text-slate-800 leading-[1.7]">{pick(p, lang)}</p>
        ))}
      </div>

      {/* Responsibility list */}
      {respItems.length > 0 && (
        <div className="mb-8" data-testid="safety-responsibility">
          {lesson.responsibility_intro && (
            <p className="text-[18px] text-slate-900 font-semibold mb-3 leading-relaxed">
              {pick(lesson.responsibility_intro, lang)}
            </p>
          )}
          <BulletList items={respItems} lang={lang} />
        </div>
      )}

      {/* Emphasis quote — cyan highlighted panel */}
      {lesson.emphasis_quote && (
        <div className="mb-10 rounded-2xl bg-cyan-50 border-l-4 border-cyan-500 p-5" data-testid="safety-emphasis">
          <div className="flex items-start gap-3">
            <Info className="w-5 h-5 text-cyan-700 mt-1 flex-shrink-0" />
            <p className="text-[18px] font-bold text-slate-900 leading-snug italic">
              {pick(lesson.emphasis_quote, lang)}
            </p>
          </div>
        </div>
      )}

      {/* Section body */}
      {lesson.section_body && (
        <p className="text-[18px] text-slate-800 leading-[1.7] mb-8">{pick(lesson.section_body, lang)}</p>
      )}

      {/* Before work — checklist card */}
      {beforeItems.length > 0 && (
        <div className="mb-10 rounded-2xl border border-slate-200 bg-white p-5 sm:p-6" data-testid="safety-before-work">
          {lesson.before_work_title && (
            <div className="text-[11px] uppercase tracking-widest text-slate-600 font-black mb-3">
              {pick(lesson.before_work_title, lang)}
            </div>
          )}
          <ul className="space-y-3">
            {beforeItems.map((it, i) => (
              <li key={i} className="flex items-start gap-3">
                <div className="w-6 h-6 rounded-full bg-slate-900 flex items-center justify-center flex-shrink-0" style={{ color: "#fff" }}>
                  <CheckCircle2 className="w-3.5 h-3.5" />
                </div>
                <span className="text-[17px] text-slate-800 leading-relaxed">{pick(it, lang)}</span>
              </li>
            ))}
          </ul>
        </div>
      )}

      {/* Key cards — Identify / Stop / Report */}
      {keyCards.length > 0 && (
        <div className="mb-10" data-testid="safety-key-cards">
          {lesson.key_cards_title && (
            <p className="text-[18px] text-slate-900 font-semibold mb-4 leading-relaxed">
              {pick(lesson.key_cards_title, lang)}
            </p>
          )}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
            {keyCards.map((c, i) => {
              const Icon = SAFETY_ICONS[c.icon] || AlertTriangle;
              const clr = SAFETY_COLORS[i % SAFETY_COLORS.length];
              return (
                <div
                  key={i}
                  className={`${clr.bg} rounded-2xl p-5 ring-1 ${clr.ring}`}
                  data-testid={`safety-card-${i}`}
                >
                  <div className={`w-12 h-12 rounded-xl ${clr.iconBg} flex items-center justify-center mb-3`} style={{ color: "#fff" }}>
                    <Icon className="w-6 h-6" />
                  </div>
                  <div className={`text-[10px] uppercase tracking-widest font-black mb-1 ${clr.chip}`}>
                    {`0${i + 1}`.slice(-2)}
                  </div>
                  <div className="text-lg font-black text-slate-900 leading-tight">{pick(c.title, lang)}</div>
                  <div className="text-[15px] text-slate-800 mt-1.5 leading-snug">{pick(c.subtitle, lang)}</div>
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* Stop Work Authority — bordered panel */}
      {(lesson.stop_work_title || stopItems.length > 0) && (
        <div className="mb-10 rounded-2xl bg-slate-900 p-6" style={{ color: "#fff" }} data-testid="safety-stop-work">
          <div className="flex items-center gap-3 mb-3">
            <div className="w-10 h-10 rounded-xl bg-red-500/20 flex items-center justify-center ring-1 ring-red-500/40">
              <OctagonAlert className="w-5 h-5 text-red-400" />
            </div>
            <div>
              <div className="text-[10px] uppercase tracking-[0.25em] text-red-300 font-black">HSE</div>
              <div className="text-xl font-black">{pick(lesson.stop_work_title, lang)}</div>
            </div>
          </div>
          {lesson.stop_work_body && (
            <p className="text-[16px] text-slate-100 mb-4 leading-[1.7]">{pick(lesson.stop_work_body, lang)}</p>
          )}
          {lesson.stop_work_intro && (
            <p className="text-sm font-semibold text-red-200 mb-2">{pick(lesson.stop_work_intro, lang)}</p>
          )}
          <ul className="space-y-2">
            {stopItems.map((it, i) => (
              <li key={i} className="flex items-start gap-3">
                <span className="mt-2.5 w-1.5 h-1.5 rounded-full bg-red-400 flex-shrink-0" />
                <span className="text-[16px] text-slate-100 leading-relaxed">{pick(it, lang)}</span>
              </li>
            ))}
          </ul>
        </div>
      )}

      {/* After stopping */}
      {afterItems.length > 0 && (
        <div className="mb-10" data-testid="safety-after-stop">
          {lesson.after_stop_title && (
            <p className="text-[18px] text-slate-900 font-semibold mb-3 leading-relaxed">
              {pick(lesson.after_stop_title, lang)}
            </p>
          )}
          <BulletList items={afterItems} lang={lang} tone="amber" />
        </div>
      )}

      {/* Numbered sections — long-form curriculum lessons */}
      {sections.length > 0 && (
        <>
          {/* Compact section navigator — pills scroll horizontally on mobile */}
          <div
            className="sticky top-2 z-10 -mx-2 mb-6 bg-white/95 backdrop-blur rounded-2xl border border-slate-200 p-2 flex items-center gap-2 overflow-x-auto"
            data-testid="section-toc"
          >
            <span className="text-[11px] uppercase tracking-widest text-slate-500 font-black pl-2 pr-1 flex-shrink-0">
              §
            </span>
            {sections.map((_, i) => (
              <a
                key={i}
                href={`#section-${i + 1}`}
                onClick={(e) => {
                  e.preventDefault();
                  document.getElementById(`section-${i + 1}`)?.scrollIntoView({ behavior: "smooth", block: "start" });
                }}
                className="flex-shrink-0 w-8 h-8 rounded-lg bg-slate-100 hover:bg-slate-900 hover:text-white flex items-center justify-center text-[13px] font-black text-slate-700 transition"
                data-testid={`toc-pill-${i + 1}`}
              >
                {i + 1}
              </a>
            ))}
          </div>
          <div className="mb-10 space-y-8">
            {sections.map((sec, i) => (
              <section
                key={i}
                id={`section-${i + 1}`}
                className="scroll-mt-24"
                data-testid={`section-${i + 1}`}
              >
                <div className="flex items-start gap-3 mb-3">
                  <div className="w-10 h-10 rounded-2xl bg-slate-900 flex items-center justify-center text-base font-black flex-shrink-0" style={{ color: "#fff" }}>
                    {i + 1}
                  </div>
                  <h3 className="text-xl sm:text-2xl font-black text-slate-900 leading-snug pt-1">{pick(sec.title, lang)}</h3>
                </div>
                {sec.body && (
                  <p className="text-[17px] text-slate-800 leading-[1.7] mb-3 pl-[52px]">{pick(sec.body, lang)}</p>
                )}
                {sec.items && sec.items.length > 0 && (
                  <div className="pl-[52px]">
                    <BulletList items={sec.items} lang={lang} />
                  </div>
                )}
                {sec.action_items && sec.action_items.length > 0 && (
                  <div className="mt-4 ml-[52px] rounded-2xl bg-amber-50 border-l-4 border-amber-500 p-4" data-testid={`section-${i + 1}-action`}>
                    {sec.action_title && (
                      <p className="text-[11px] uppercase tracking-widest text-amber-800 font-black mb-2">
                        {pick(sec.action_title, lang)}
                      </p>
                    )}
                    <ol className="space-y-2 list-decimal list-inside">
                      {sec.action_items.map((it, j) => (
                        <li key={j} className="text-[15px] text-slate-800 leading-relaxed">
                          {pick(it, lang)}
                        </li>
                      ))}
                    </ol>
                  </div>
                )}
                {sec.note && (
                  <div className="mt-3 ml-[52px] rounded-xl bg-slate-100 border border-slate-200 p-3 flex items-start gap-2">
                    <Info className="w-4 h-4 text-slate-600 mt-0.5 flex-shrink-0" />
                    <p className="text-[14px] text-slate-700 leading-relaxed italic">{pick(sec.note, lang)}</p>
                  </div>
                )}
              </section>
            ))}
          </div>
        </>
      )}

      {/* Warning line — big red bar */}
      {lesson.warning && (
        <div className="mb-6 rounded-2xl bg-red-50 border-l-4 border-red-500 p-5 flex items-start gap-3" data-testid="safety-warning">
          <AlertTriangle className="w-6 h-6 text-red-600 flex-shrink-0 mt-0.5" />
          <p className="text-[18px] font-black text-red-900 leading-snug">
            {pick(lesson.warning, lang)}
          </p>
        </div>
      )}

      {/* Disclaimer + source */}
      {(lesson.disclaimer || lesson.source_note) && (
        <div className="mt-6 border-t border-slate-200 pt-4 space-y-2">
          {lesson.disclaimer && (
            <p className="text-[13px] text-slate-700 leading-relaxed italic" data-testid="safety-disclaimer">
              {pick(lesson.disclaimer, lang)}
            </p>
          )}
          {lesson.source_note && (
            <p className="text-[12px] text-slate-500 leading-relaxed">
              {pick(lesson.source_note, lang)}
            </p>
          )}
        </div>
      )}
    </div>
  );
}
function LessonText({ lesson, lang, t }) {
  return (
    <>
      <p className="text-base text-slate-700 whitespace-pre-wrap leading-relaxed">{pick(lesson.body, lang)}</p>
      <TakeawayBox lesson={lesson} lang={lang} t={t} />
    </>
  );
}
function LessonChecklist({ lesson, lang, t }) {
  return (
    <div>
      {lesson.body && <p className="text-base text-slate-700 mb-4 leading-relaxed whitespace-pre-wrap">{pick(lesson.body, lang)}</p>}
      <ul className="space-y-2.5">
        {(lesson.items || []).map((it, i) => (
          <li key={i} className="flex items-start gap-3 bg-slate-50 border border-slate-200 rounded-xl px-4 py-3">
            <div className="w-6 h-6 rounded-full bg-slate-900 flex items-center justify-center flex-shrink-0 text-[10px] font-black" style={{ color: "#fff" }}>
              {i + 1}
            </div>
            <span className="text-sm text-slate-800 leading-relaxed">{pick(it, lang)}</span>
          </li>
        ))}
      </ul>
      {lesson.note && (
        <div className="mt-5 bg-amber-50 border-l-4 border-amber-400 rounded-r-lg p-4 text-sm text-amber-900">
          <div className="text-[10px] uppercase font-bold tracking-widest mb-1">{t("kurs.note")}</div>
          {pick(lesson.note, lang)}
        </div>
      )}
      <TakeawayBox lesson={lesson} lang={lang} t={t} />
    </div>
  );
}
function LessonHierarchy({ lesson, lang, t }) {
  return (
    <div>
      {lesson.body && <p className="text-base text-slate-700 mb-4 leading-relaxed whitespace-pre-wrap">{pick(lesson.body, lang)}</p>}
      <div className="flex flex-col gap-2">
        {(lesson.chain || []).map((step, i) => (
          <React.Fragment key={step}>
            <div className={`flex items-center gap-3 rounded-xl px-4 py-3 border ${i === 0 ? "bg-cyan-50 border-cyan-300" : "bg-white border-slate-200"}`}>
              <div className="w-8 h-8 rounded-full bg-slate-900 flex items-center justify-center flex-shrink-0 text-xs font-black" style={{ color: "#fff" }}>{i + 1}</div>
              <span className="text-sm font-bold text-slate-900">{step}</span>
              {i === 0 && <span className="ml-auto text-[10px] uppercase tracking-widest text-cyan-700 font-bold">{t("kurs.you_are_here")}</span>}
            </div>
            {i < lesson.chain.length - 1 && <div className="text-center text-slate-300 -my-1"><ChevronDown className="w-4 h-4 mx-auto" /></div>}
          </React.Fragment>
        ))}
      </div>
      <TakeawayBox lesson={lesson} lang={lang} t={t} />
    </div>
  );
}
function LessonCards({ lesson, lang, t }) {
  return (
    <div>
      {lesson.body && <p className="text-base text-slate-700 mb-4 leading-relaxed whitespace-pre-wrap">{pick(lesson.body, lang)}</p>}
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
        {(lesson.cards || []).map((c, i) => {
          const colors = ["bg-cyan-500", "bg-amber-500", "bg-emerald-500", "bg-indigo-500"];
          return (
            <div key={i} className={`${colors[i % colors.length]} rounded-2xl p-5`} style={{ color: "#fff" }}>
              <Sparkles className="w-5 h-5 mb-3 opacity-80" />
              <div className="text-lg font-black leading-tight">{pick(c.title, lang)}</div>
              <div className="text-sm opacity-90 mt-1 leading-relaxed">{pick(c.subtitle, lang)}</div>
            </div>
          );
        })}
      </div>
      <TakeawayBox lesson={lesson} lang={lang} t={t} />
    </div>
  );
}
function LessonProductivity({ lesson, lang, t }) {
  return (
    <div>
      {lesson.body && <p className="text-base text-slate-700 mb-4 whitespace-pre-wrap leading-relaxed">{pick(lesson.body, lang)}</p>}
      <div className="bg-white border border-slate-200 rounded-2xl overflow-hidden mb-4">
        <table className="w-full">
          <thead className="bg-slate-900" style={{ color: "#fff" }}>
            <tr>
              <th className="text-left px-4 py-2 text-xs uppercase tracking-widest font-bold">Team</th>
              <th className="text-right px-4 py-2 text-xs uppercase tracking-widest font-bold">Target</th>
            </tr>
          </thead>
          <tbody>
            {(lesson.table || []).map((r, i) => (
              <tr key={i} className={i % 2 === 0 ? "bg-white" : "bg-slate-50"}>
                <td className="px-4 py-3 text-sm text-slate-800">{pick(r.team, lang)}</td>
                <td className="px-4 py-3 text-sm font-black text-slate-900 text-right">{r.target}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      {Array.isArray(lesson.calc_sections) && lesson.calc_sections.map((sec, i) => (
        <div key={i} className="bg-cyan-50 border border-cyan-100 rounded-2xl p-4 mb-4" data-testid={`calc-section-${i}`}>
          <div className="text-sm font-black text-cyan-800 mb-2">{pick(sec.title, lang)}</div>
          <ul className="space-y-1.5">
            {(sec.lines || []).map((ln, j) => (
              <li key={j} className="flex items-start gap-2 text-sm text-slate-800 leading-relaxed">
                <span className="text-cyan-600 mt-0.5">▸</span>
                <span>{pick(ln, lang)}</span>
              </li>
            ))}
          </ul>
        </div>
      ))}
      {lesson.note && (
        <div className="bg-red-50 border-l-4 border-red-400 rounded-r-lg p-4 text-sm text-red-900">
          <div className="text-[10px] uppercase font-bold tracking-widest mb-1">{t("kurs.warning")}</div>
          {pick(lesson.note, lang)}
        </div>
      )}
      <TakeawayBox lesson={lesson} lang={lang} t={t} />
    </div>
  );
}

const RENDERERS = {
  welcome:       LessonWelcome,
  intro_hero:    LessonIntroHero,
  safety_lesson: LessonSafety,
  text:          LessonText,
  checklist:     LessonChecklist,
  hierarchy:     LessonHierarchy,
  cards:         LessonCards,
  productivity:  LessonProductivity,
};

/* ============================================================
   Quiz view
============================================================ */
function QuizView({ course, quiz, onFinished, authHeader, t, lang }) {
  const [answers, setAnswers] = useState({});
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState(null);

  const submit = async () => {
    setBusy(true);
    try {
      const payload = { answers: quiz.map((q) => ({ question_id: q.id, selected_index: answers[q.id] ?? -1 })) };
      const { data } = await axios.post(`${API}/doserne/kurs/${course.code}/quiz/submit`, payload, authHeader());
      setResult(data);
      if (data.passed) toast.success(t("kurs.quiz_passed"));
      else toast.error(t("kurs.quiz_failed"));
    } catch (err) { toast.error(err?.response?.data?.detail || t("common.error")); }
    setBusy(false);
  };

  const allAnswered = quiz.every((q) => answers[q.id] !== undefined);

  if (result) {
    return (
      <div className="bg-white border border-slate-200 rounded-2xl p-6" data-testid="quiz-result">
        <div className={`w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-4 ${result.passed ? "bg-emerald-500" : "bg-red-500"}`} style={{ color: "#fff" }}>
          {result.passed ? <CheckCircle2 className="w-8 h-8" /> : <XCircle className="w-8 h-8" />}
        </div>
        <h3 className="text-2xl font-black text-slate-900 text-center mb-1">{result.passed ? t("kurs.quiz_passed") : t("kurs.quiz_failed")}</h3>
        <p className="text-sm text-slate-500 text-center mb-4">
          {t("kurs.quiz_score_line", { correct: result.correct, total: result.total, pct: Math.round(result.score * 100), threshold: Math.round(result.threshold * 100) })}
        </p>

        {result.passed && result.certificate && (
          <div className="bg-gradient-to-br from-cyan-50 to-emerald-50 border-2 border-cyan-200 rounded-xl p-4 mb-4 text-center">
            <Award className="w-8 h-8 text-cyan-600 mx-auto mb-2" />
            <div className="text-xs uppercase tracking-widest text-slate-500 font-bold">{t("kurs.cert.title")}</div>
            <div className="text-lg font-black text-slate-900 font-mono">{result.certificate.cert_number}</div>
            <button
              onClick={async () => {
                try {
                  const { data } = await axios.get(`${API}/doserne/certificates/${result.certificate.id}/pdf`, {
                    ...authHeader(), responseType: "blob",
                  });
                  const url = URL.createObjectURL(data);
                  window.open(url, "_blank");
                  setTimeout(() => URL.revokeObjectURL(url), 60000);
                } catch { toast.error(t("common.error")); }
              }}
              type="button"
              className="inline-flex items-center gap-1.5 mt-2 px-4 py-2 rounded-lg bg-slate-900 text-sm font-semibold"
              style={{ color: "#fff" }}
              data-testid="download-cert"
            >
              <Award className="w-4 h-4" /> {t("kurs.cert.open_pdf")}
            </button>
          </div>
        )}

        <div className="mt-6 space-y-2">
          {(result.detail || []).map((d, i) => (
            <div key={d.question_id} className={`text-sm border rounded-lg p-3 ${d.is_correct ? "border-emerald-200 bg-emerald-50" : "border-red-200 bg-red-50"}`}>
              <div className="flex items-start gap-2">
                {d.is_correct ? <CheckCircle2 className="w-4 h-4 text-emerald-600 mt-0.5 flex-shrink-0" /> : <XCircle className="w-4 h-4 text-red-600 mt-0.5 flex-shrink-0" />}
                <div>
                  <div className="font-bold text-slate-900">Q{i + 1}. {pick(quiz[i]?.question, lang) || d.question}</div>
                  {!d.is_correct && quiz[i]?.options[d.correct_index]?.text && (
                    <div className="text-slate-700 mt-1">{t("kurs.quiz_correct_answer")}: <strong>{pick(quiz[i].options[d.correct_index].text, lang)}</strong></div>
                  )}
                </div>
              </div>
            </div>
          ))}
        </div>

        <div className="mt-6 flex gap-2">
          {!result.passed && (
            <button onClick={() => { setResult(null); setAnswers({}); }} className="flex-1 py-2.5 rounded-lg bg-slate-900 text-sm font-semibold" style={{ color: "#fff" }} data-testid="retry-quiz">
              {t("kurs.quiz_retry")}
            </button>
          )}
          <button onClick={() => onFinished(result)} className="flex-1 py-2.5 rounded-lg border border-slate-300 text-slate-700 text-sm font-semibold hover:bg-slate-50" data-testid="finish-quiz">
            {t("common.finish")}
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      {quiz.map((q, qi) => (
        <div key={q.id} className="bg-white border border-slate-200 rounded-2xl p-5" data-testid={`quiz-q-${q.id}`}>
          <div className="text-[10px] uppercase tracking-widest text-slate-500 font-bold mb-1">{t("kurs.quiz_question_n", { n: qi + 1, total: quiz.length })}</div>
          <h3 className="text-lg font-bold text-slate-900 mb-4 leading-tight">{pick(q.question, lang)}</h3>
          <div className="space-y-2">
            {q.options.map((o, oi) => {
              const selected = answers[q.id] === oi;
              return (
                <button
                  key={oi}
                  onClick={() => setAnswers({ ...answers, [q.id]: oi })}
                  type="button"
                  className={`w-full text-left px-4 py-3 rounded-xl border-2 transition ${selected ? "border-cyan-500 bg-cyan-50" : "border-slate-200 hover:border-slate-300 bg-white"}`}
                  data-testid={`quiz-opt-${q.id}-${oi}`}
                >
                  <div className="flex items-center gap-3">
                    <div className={`w-5 h-5 rounded-full border-2 flex-shrink-0 flex items-center justify-center ${selected ? "border-cyan-500 bg-cyan-500" : "border-slate-300"}`}>
                      {selected && <div className="w-2 h-2 rounded-full bg-white" />}
                    </div>
                    <span className={`text-sm ${selected ? "text-slate-900 font-semibold" : "text-slate-700"}`}>{pick(o.text, lang)}</span>
                  </div>
                </button>
              );
            })}
          </div>
        </div>
      ))}
      <button
        onClick={submit}
        disabled={busy || !allAnswered}
        className="w-full py-3 bg-slate-900 hover:bg-slate-800 disabled:bg-slate-300 rounded-lg font-semibold text-sm shadow-sm flex items-center justify-center gap-2"
        style={{ color: "#fff" }}
        data-testid="submit-quiz"
      >
        {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <>{t("kurs.quiz_submit")} <ArrowRight className="w-4 h-4" /></>}
      </button>
      {!allAnswered && <p className="text-xs text-slate-500 text-center">{t("kurs.quiz_answer_all")}</p>}
    </div>
  );
}

/* ============================================================
   AdminCoursePdfDownload — admin-only widget that lets Bogdan
   download the entire course (all lessons + quiz with correct
   answers marked) as an A4 PDF or an editable .docx in the
   chosen language. Sits in the course-index header card and
   is hidden from workers.
============================================================ */
function AdminCoursePdfDownload({ course, lang }) {
  const { authHeader } = useDoserneAuth();
  const [busy, setBusy] = useState(false);
  const [pickedLang, setPickedLang] = useState(lang || "no");
  const [pickedFormat, setPickedFormat] = useState("pdf"); // "pdf" | "docx"

  const LABELS = {
    no: { title: "Admin — last ned kurset", hint: "PDF (utskriftsklar) eller Word (redigerbar) med alle leksjoner + fasit.", btn: "Last ned", err: "Kunne ikke laste ned" },
    ro: { title: "Admin — descarcă cursul", hint: "PDF (gata de tipărit) sau Word (editabil) cu toate lecțiile + răspunsurile.", btn: "Descarcă", err: "Descărcarea a eșuat" },
    en: { title: "Admin — download course", hint: "PDF (print-ready) or Word (editable) with every lesson + answer key.", btn: "Download", err: "Download failed" },
  };
  const L = LABELS[lang] || LABELS.no;

  const download = async () => {
    setBusy(true);
    try {
      const ext = pickedFormat === "docx" ? "docx" : "pdf";
      const res = await axios.get(
        `${API}/doserne/admin/kurs/${course.code}/export.${ext}?lang=${pickedLang}`,
        { ...authHeader(), responseType: "blob" },
      );
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a");
      a.href = url;
      a.download = `doserne-kurs-${course.code}-${pickedLang}.${ext}`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      setTimeout(() => URL.revokeObjectURL(url), 60000);
    } catch {
      toast.error(L.err);
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="mt-4 rounded-xl border border-amber-200 bg-amber-50 p-3 sm:p-4" data-testid="admin-course-download">
      <div className="flex items-start gap-3">
        <div className="w-9 h-9 rounded-lg bg-amber-500 flex items-center justify-center flex-shrink-0" style={{ color: "#fff" }}>
          <Download className="w-4 h-4" />
        </div>
        <div className="flex-1 min-w-0">
          <div className="text-[10px] uppercase tracking-widest text-amber-800 font-black">{L.title}</div>
          <div className="text-[13px] text-slate-800 mt-0.5 leading-snug">{L.hint}</div>
          <div className="flex flex-wrap items-center gap-2 mt-3">
            <select
              value={pickedFormat}
              onChange={(e) => setPickedFormat(e.target.value)}
              className="text-[13px] px-2 py-1.5 rounded-lg border border-amber-300 bg-white text-slate-800 focus:outline-none focus:ring-2 focus:ring-amber-500/40"
              data-testid="admin-course-download-format"
            >
              <option value="pdf">PDF</option>
              <option value="docx">Word (.docx)</option>
            </select>
            <select
              value={pickedLang}
              onChange={(e) => setPickedLang(e.target.value)}
              className="text-[13px] px-2 py-1.5 rounded-lg border border-amber-300 bg-white text-slate-800 focus:outline-none focus:ring-2 focus:ring-amber-500/40"
              data-testid="admin-course-download-lang"
            >
              <option value="no">Norsk</option>
              <option value="ro">Română</option>
              <option value="en">English</option>
            </select>
            <button
              type="button"
              onClick={download}
              disabled={busy}
              className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-slate-900 hover:bg-slate-800 disabled:opacity-50 text-white text-[13px] font-bold"
              data-testid="admin-course-download-btn"
            >
              {busy ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Download className="w-3.5 h-3.5" />}
              {L.btn} {pickedFormat.toUpperCase()}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

/* ============================================================
   Course Index (lesson menu) — numbered cards for every lesson.
   Locked state respects prior-lesson pass. Passed lessons show a
   green check; the "next" lesson gets a cyan CTA styling.
============================================================ */
function LessonIndex({ course, lessons, quiz, progress, lang, t, onOpenLesson, finalGrading }) {
  const { user: currentUser } = useDoserneAuth();
  const isAdmin = !!currentUser?.is_admin;
  const completed = new Set(progress?.completed_lesson_ids || []);
  const passed = new Set(progress?.passed_lesson_ids || []);
  const quizByLesson = {};
  (quiz || []).forEach((q) => {
    if (q.lesson_order != null) {
      quizByLesson[q.lesson_order] = (quizByLesson[q.lesson_order] || 0) + 1;
    }
  });

  const isLessonPassed = (l) => {
    // Final-grading courses are read-through (graded once at the end):
    // "done" means the lesson has been read.
    if (finalGrading) return completed.has(l.id);
    if (passed.has(l.id)) return true;
    // Lesson has no quiz — completing (reading) it is enough.
    if (!quizByLesson[l.order]) return completed.has(l.id);
    return false;
  };

  // Locking chain: lesson N unlocked when lesson N-1 is passed.
  const unlocked = new Set();
  lessons.forEach((l, i) => {
    if (i === 0) unlocked.add(l.id);
    else if (isLessonPassed(lessons[i - 1])) unlocked.add(l.id);
  });

  const passedCount = lessons.filter(isLessonPassed).length;
  const total = lessons.length;

  return (
    <div>
      <div className="bg-white border border-slate-200 rounded-2xl p-5 sm:p-6 mb-4">
        <div className="text-4xl mb-2">{course.cover_emoji}</div>
        <h1 className="text-2xl sm:text-3xl font-black text-slate-900 tracking-tight leading-tight">{pick(course.title, lang)}</h1>
        <p className="text-[15px] text-slate-600 mt-1">{pick(course.subtitle, lang)}</p>
        {isAdmin && <AdminCoursePdfDownload course={course} lang={lang} />}
        <div className="mt-4">
          <div className="flex items-center justify-between text-[11px] font-bold uppercase tracking-wider text-slate-600 mb-1">
            <span data-testid="course-progress-label">{t("kurs.lesson_x_of_y", { current: passedCount, total })}</span>
            <span data-testid="course-progress-pct">{total ? Math.round((passedCount / total) * 100) : 0}%</span>
          </div>
          <div className="h-2 bg-slate-100 rounded-full overflow-hidden">
            <div className="h-full bg-cyan-500 transition-all" style={{ width: `${total ? (passedCount / total) * 100 : 0}%` }} />
          </div>
        </div>
      </div>

      <CoursePassedBanner course={course} lang={lang} t={t} passedCount={passedCount} total={total} />

      <div className="text-[10px] uppercase tracking-widest text-slate-500 font-bold mb-3">
        {t("kurs.lessons_in_this_course")}
      </div>
      <div className="space-y-3" data-testid="lesson-index">
        {lessons.map((l, i) => {
          const done = isLessonPassed(l);
          const isUnlocked = unlocked.has(l.id);
          const isNext = isUnlocked && !done;
          return (
            <button
              key={l.id}
              type="button"
              onClick={() => isUnlocked && onOpenLesson(i)}
              disabled={!isUnlocked}
              className={`w-full text-left rounded-2xl border p-4 sm:p-5 flex items-center gap-4 transition
                ${done ? "border-emerald-200 bg-emerald-50/40" : ""}
                ${isNext ? "border-cyan-300 bg-white hover:border-cyan-500 hover:shadow-sm" : ""}
                ${!isUnlocked ? "border-slate-200 bg-slate-50 opacity-60 cursor-not-allowed" : ""}
              `}
              data-testid={`lesson-card-${l.order}`}
            >
              <div className={`w-11 h-11 rounded-2xl flex items-center justify-center flex-shrink-0 text-base font-black
                ${done ? "bg-emerald-500" : isNext ? "bg-slate-900" : "bg-slate-300"}
              `} style={{ color: "#fff" }}>
                {done ? <CheckCircle2 className="w-5 h-5" /> : !isUnlocked ? <Lock className="w-4 h-4" /> : l.order}
              </div>
              <div className="flex-1 min-w-0">
                <div className="text-[10px] uppercase tracking-widest text-slate-500 font-black">
                  {t("kurs.lesson_shortcut", { n: l.order })}
                </div>
                <div className="text-[17px] font-black text-slate-900 leading-tight">{pick(l.title, lang)}</div>
                <div className="text-[13px] text-slate-600 mt-0.5">
                  {quizByLesson[l.order]
                    ? t("kurs.n_questions", { n: quizByLesson[l.order] })
                    : t("kurs.no_quiz")}
                  {done && <span className="text-emerald-700 font-bold ml-2">· {t("kurs.passed_badge")}</span>}
                </div>
              </div>
              {isUnlocked && (
                <ArrowRight className={`w-5 h-5 flex-shrink-0 ${done ? "text-emerald-500" : "text-slate-400"}`} />
              )}
            </button>
          );
        })}
      </div>
    </div>
  );
}

/* ============================================================
   Mini-quiz for a single lesson.
   Submits to POST /kurs/{code}/lessons/{lesson_order}/quiz which
   grades only the questions tagged with that lesson_order, and
   returns per-question feedback + pass state.
============================================================ */
function LessonMiniQuiz({ course, lesson, questions, threshold, authHeader, onClose, t, lang, collectOnly, onCollect, externalBusy }) {
  const [answers, setAnswers] = useState({});
  const [current, setCurrent] = useState(0);
  const [result, setResult] = useState(null);
  const [busy, setBusy] = useState(false);
  const total = questions.length;
  const q = questions[current];
  const chosen = q ? answers[q.id] : null;
  const isLast = current === total - 1;
  const allAnswered = questions.every((qq) => answers[qq.id] != null);

  const submit = async () => {
    // Final-grading courses: don't grade per lesson — hand answers to the
    // parent, which submits everything at the end of the course.
    if (collectOnly) {
      onCollect(questions.map((qq) => ({ question_id: qq.id, option_index: answers[qq.id] })));
      return;
    }
    setBusy(true);
    try {
      const { data } = await axios.post(
        `${API}/doserne/kurs/${course.code}/lessons/${lesson.order}/quiz`,
        {
          answers: questions.map((qq) => ({
            question_id: qq.id,
            option_index: answers[qq.id],
          })),
        },
        authHeader(),
      );
      // On pass: skip the result screen and jump straight to the next lesson.
      // On fail: show the retry screen so the user knows to re-read.
      if (data?.passed) { onClose(true); return; }
      setResult(data);
    } catch { toast.error(t("common.error")); }
    setBusy(false);
  };

  // ---------- Result screen ----------
  if (result) {
    const scorePct = Math.round(result.score * 100);
    const finishedAt = new Date().toLocaleDateString(
      lang === "no" ? "nb-NO" : lang === "ro" ? "ro-RO" : "en-GB",
      { day: "2-digit", month: "long", year: "numeric" },
    );

    if (result.passed) {
      return (
        <div className="bg-white border border-slate-200 rounded-2xl p-6 sm:p-8 text-center max-w-xl mx-auto" data-testid="mini-quiz-result">
          <div className="w-20 h-20 rounded-full flex items-center justify-center mx-auto mb-4 bg-emerald-500" style={{ color: "#fff" }}>
            <CheckCircle2 className="w-10 h-10" />
          </div>
          <div className="text-[11px] uppercase tracking-widest text-emerald-700 font-black mb-1">
            {t("kurs.mini_quiz_pass")}
          </div>
          <h3 className="text-2xl sm:text-3xl font-black text-slate-900 mb-6">
            {pick(lesson.title, lang)}
          </h3>

          <div className="grid grid-cols-2 gap-3 mb-6 max-w-md mx-auto">
            <div className="rounded-2xl bg-slate-50 border border-slate-200 p-4">
              <div className="text-[10px] uppercase tracking-widest text-slate-500 font-black">
                {t("kurs.result_score")}
              </div>
              <div className="text-2xl font-black text-slate-900 mt-1">{scorePct}%</div>
              <div className="text-[11px] text-slate-500 mt-0.5">
                {t("kurs.result_score_detail", { correct: result.correct, total: result.total })}
              </div>
            </div>
            <div className="rounded-2xl bg-slate-50 border border-slate-200 p-4">
              <div className="text-[10px] uppercase tracking-widest text-slate-500 font-black">
                {t("kurs.result_completed_on")}
              </div>
              <div className="text-[15px] font-black text-slate-900 mt-1">{finishedAt}</div>
              <div className="text-[11px] text-slate-500 mt-0.5">
                {t("kurs.result_min_pass", { pct: Math.round(threshold * 100) })}
              </div>
            </div>
          </div>

          <button
            onClick={() => onClose(true)}
            className="w-full py-3 rounded-lg bg-emerald-500 hover:bg-emerald-600 text-sm font-semibold flex items-center justify-center gap-2"
            style={{ color: "#fff" }}
            data-testid="mini-quiz-close"
          >
            {t("kurs.continue_next")} <ArrowRight className="w-4 h-4" />
          </button>
        </div>
      );
    }

    // ---------- Failure screen — no answer key shown ----------
    return (
      <div className="bg-white border border-slate-200 rounded-2xl p-6 sm:p-8 text-center max-w-xl mx-auto" data-testid="mini-quiz-result">
        <div className="w-20 h-20 rounded-full flex items-center justify-center mx-auto mb-4 bg-red-500" style={{ color: "#fff" }}>
          <XCircle className="w-10 h-10" />
        </div>
        <div className="text-[11px] uppercase tracking-widest text-red-700 font-black mb-1">
          {t("kurs.mini_quiz_retry_needed")}
        </div>
        <h3 className="text-2xl sm:text-3xl font-black text-slate-900 mb-4">
          {pick(lesson.title, lang)}
        </h3>
        <div className="rounded-2xl bg-red-50 border border-red-200 p-4 mb-6 max-w-sm mx-auto">
          <div className="text-[10px] uppercase tracking-widest text-red-700 font-black">
            {t("kurs.result_score")}
          </div>
          <div className="text-3xl font-black text-red-900 mt-1">{scorePct}%</div>
          <div className="text-[12px] text-red-800 mt-1">
            {t("kurs.result_min_pass", { pct: Math.round(threshold * 100) })}
          </div>
        </div>
        <p className="text-[15px] text-slate-700 leading-relaxed mb-6">
          {t("kurs.mini_quiz_reread_hint")}
        </p>
        <button
          onClick={() => onClose(false)}
          className="w-full py-3 rounded-lg bg-slate-900 hover:bg-slate-800 text-sm font-semibold flex items-center justify-center gap-2"
          style={{ color: "#fff" }}
          data-testid="mini-quiz-close"
        >
          <ArrowLeft className="w-4 h-4" /> {t("kurs.mini_quiz_retry_lesson")}
        </button>
      </div>
    );
  }

  // ---------- One-question-per-screen quiz ----------
  return (
    <div className="bg-white border border-slate-200 rounded-2xl p-5 sm:p-6 max-w-xl mx-auto" data-testid="mini-quiz">
      <div className="flex items-center justify-between mb-4">
        <div className="text-[11px] uppercase tracking-widest text-cyan-700 font-black">
          {t("kurs.quiz_question_n", { n: current + 1, total })}
        </div>
        <div className="text-[11px] text-slate-500 font-bold">{pick(lesson.title, lang)}</div>
      </div>

      {/* Progress dots */}
      <div className="flex items-center gap-1.5 mb-6" data-testid="mini-quiz-dots">
        {questions.map((qq, i) => {
          const answered = answers[qq.id] != null;
          const isCurrent = i === current;
          return (
            <div
              key={qq.id}
              className={`h-1.5 flex-1 rounded-full transition ${
                isCurrent ? "bg-slate-900" : answered ? "bg-cyan-500" : "bg-slate-200"
              }`}
            />
          );
        })}
      </div>

      <h3 className="text-[18px] sm:text-[20px] font-bold text-slate-900 mb-5 leading-snug">
        {pick(q.question, lang)}
      </h3>

      <div className="space-y-2 mb-6">
        {q.options.map((o, oi) => {
          const selected = chosen === oi;
          return (
            <button
              key={oi}
              type="button"
              onClick={() => setAnswers({ ...answers, [q.id]: oi })}
              className={`w-full text-left px-4 py-3.5 rounded-xl border-2 transition ${
                selected ? "border-cyan-500 bg-cyan-50" : "border-slate-200 hover:border-slate-300 bg-white"
              }`}
              data-testid={`mini-quiz-opt-${q.id}-${oi}`}
            >
              <div className="flex items-center gap-3">
                <div className={`w-6 h-6 rounded-full border-2 flex-shrink-0 flex items-center justify-center ${selected ? "border-cyan-500 bg-cyan-500" : "border-slate-300"}`}>
                  {selected && <div className="w-2.5 h-2.5 rounded-full bg-white" />}
                </div>
                <span className={`text-[15px] ${selected ? "text-slate-900 font-semibold" : "text-slate-700"}`}>{pick(o.text, lang)}</span>
              </div>
            </button>
          );
        })}
      </div>

      <div className="flex gap-2">
        <button
          onClick={() => (current === 0 ? onClose(false) : setCurrent(current - 1))}
          className="px-4 py-2.5 rounded-lg border border-slate-300 text-slate-700 text-sm font-semibold hover:bg-slate-50"
          data-testid="mini-quiz-prev"
        >
          <ArrowLeft className="w-4 h-4 inline mr-1" />
          {current === 0 ? t("kurs.back_to_lesson") : t("common.previous")}
        </button>
        {!isLast && (
          <button
            onClick={() => setCurrent(current + 1)}
            disabled={chosen == null}
            className="flex-1 py-2.5 rounded-lg bg-slate-900 hover:bg-slate-800 disabled:bg-slate-300 text-sm font-semibold flex items-center justify-center gap-2"
            style={{ color: "#fff" }}
            data-testid="mini-quiz-next"
          >
            {t("kurs.next_question")} <ArrowRight className="w-4 h-4" />
          </button>
        )}
        {isLast && (
          <button
            onClick={submit}
            disabled={busy || externalBusy || !allAnswered}
            className="flex-1 py-2.5 rounded-lg bg-emerald-500 hover:bg-emerald-600 disabled:bg-slate-300 text-sm font-semibold flex items-center justify-center gap-2"
            style={{ color: "#fff" }}
            data-testid="mini-quiz-submit"
          >
            {(busy || externalBusy) ? <Loader2 className="w-4 h-4 animate-spin" /> : <>{collectOnly ? t("kurs.continue_next") : t("kurs.quiz_submit")} <ArrowRight className="w-4 h-4" /></>}
          </button>
        )}
      </div>
    </div>
  );
}

export default function DoserneCourseView() {
  const { code } = useParams();
  const { user, loading, authHeader } = useDoserneAuth();
  const { t, lang } = useLang();
  const [data, setData] = useState(null);
  const [busy, setBusy] = useState(true);
  const [current, setCurrent] = useState(0);
  const [phase, setPhase] = useState("index"); // "index" | "lesson" | "lesson_quiz" | "final_result" | "finished"
  const [finished, setFinished] = useState(false);
  const [collected, setCollected] = useState({});
  const [finalResult, setFinalResult] = useState(null);
  const [finalBusy, setFinalBusy] = useState(false);
  const isAdmin = !!user?.is_admin;

  const load = async () => {
    setBusy(true);
    try {
      const { data } = await axios.get(`${API}/doserne/kurs/${code}`, authHeader());
      setData(data);
    } catch { toast.error(t("common.error")); }
    setBusy(false);
  };
  useEffect(() => { if (user) load(); }, [user, code]);

  if (loading) return <div className="min-h-screen flex items-center justify-center bg-[#fafaf7]"><Loader2 className="w-8 h-8 animate-spin text-slate-400" /></div>;
  if (!user) return <Navigate to="/doserne/logg-inn" replace />;

  // Wait for the course payload before enforcing the visibility gate —
  // otherwise course #1 (HSE intro, unlocked for everyone) shows the
  // "Not enabled yet" wall for a split second even for eligible workers.
  if (busy || !data) {
    return (
      <DoserneShell>
        <div className="flex justify-center py-10"><Loader2 className="w-6 h-6 animate-spin text-slate-400" /></div>
      </DoserneShell>
    );
  }

  // All courses are open. Prerequisite (sequential) locking is still
  // enforced below via the `locked` flag returned by the backend.
  const { course, lessons, quiz, locked, progress } = data;

  const markLessonDone = async (lesson) => {
    try { await axios.post(`${API}/doserne/kurs/${code}/lessons/complete`, { lesson_id: lesson.id }, authHeader()); } catch { /* silent — completion is optimistic */ }
  };

  if (locked) {
    return (
      <DoserneShell>
        <Link to="/doserne/kurs" className="inline-flex items-center gap-1 text-xs text-slate-500 hover:text-slate-900 mb-3"><ArrowLeft className="w-3.5 h-3.5" /> {t("common.back")}</Link>
        <div className="bg-white border-2 border-dashed border-slate-200 rounded-2xl p-8 text-center">
          <Lock className="w-10 h-10 text-slate-300 mx-auto mb-3" />
          <h1 className="text-xl font-black text-slate-900 mb-1">{pick(course.title, lang)}</h1>
          <p className="text-sm text-slate-500">{t("kurs.locked_page.hint", { prereqs: (course.prerequisites || []).join(", ") })}</p>
        </div>
      </DoserneShell>
    );
  }

  // Show a lesson-index landing when: many lessons OR any question is
  // tagged to a specific lesson. Single-lesson courses without a quiz
  // (like the welcome) go straight into the reading view.
  const hasPerLessonQuiz = (quiz || []).some((q) => q.lesson_order != null);
  const useIndex = lessons.length > 1 || hasPerLessonQuiz;
  const finalGrading = course?.grading_mode === "final";

  const submitFinal = async (answersMap) => {
    setFinalBusy(true);
    try {
      const payload = { answers: (quiz || []).map((q) => ({ question_id: q.id, selected_index: answersMap[q.id] })) };
      const { data: res } = await axios.post(`${API}/doserne/kurs/${code}/quiz/submit`, payload, authHeader());
      setFinalResult(res);
      setPhase("final_result");
    } catch { toast.error(t("common.error")); }
    setFinalBusy(false);
  };
  const restartCourse = () => { setCollected({}); setFinalResult(null); setCurrent(0); setPhase("lesson"); };

  // Auto-skip index for single-lesson quiz-less courses.
  if (!useIndex && phase === "index") {
    setPhase("lesson");
    return null;
  }

  const currentLesson = lessons[current];
  const currentLessonQuestions = (quiz || []).filter((q) => q.lesson_order === (currentLesson && currentLesson.order));
  const hasLessonQuiz = currentLessonQuestions.length > 0;
  // Final-grading courses: lessons are read-through only, and ALL questions are
  // asked together in one final test at the end (no per-lesson quiz).
  const showLessonQuiz = hasLessonQuiz && !finalGrading;
  const hasFinalQuiz = finalGrading && (quiz || []).length > 0;

  const openLesson = (idx) => {
    setCurrent(idx);
    setPhase("lesson");
  };

  return (
    <DoserneShell>
      <Link
        to={phase === "index" ? "/doserne/kurs" : "#"}
        onClick={(e) => {
          if (phase !== "index") { e.preventDefault(); setPhase("index"); }
        }}
        className="inline-flex items-center gap-1 text-xs text-slate-500 hover:text-slate-900 mb-3"
        data-testid="course-back"
      >
        <ArrowLeft className="w-3.5 h-3.5" /> {phase === "index" ? t("common.back") : t("kurs.back_to_index")}
      </Link>

      {phase === "index" && (
        <LessonIndex
          course={course}
          lessons={lessons}
          quiz={quiz}
          progress={progress}
          lang={lang}
          t={t}
          onOpenLesson={openLesson}
          finalGrading={finalGrading}
        />
      )}

      {phase === "lesson" && currentLesson && (() => {
        const Renderer = RENDERERS[currentLesson.kind] || LessonText;
        const isHero = currentLesson.kind === "intro_hero" || currentLesson.kind === "safety_lesson";
        const isLast = current === lessons.length - 1;
        const completeLabel = showLessonQuiz
          ? t("kurs.continue_to_quiz")
          : isLast
            ? (hasFinalQuiz ? t("kurs.continue_to_quiz") : (pick(currentLesson.complete_label, lang) || t("common.finish")))
            : (pick(currentLesson.complete_label, lang) || t("kurs.next_lesson"));
        return (
          <div
            className={isHero ? "bg-white border border-slate-200 rounded-2xl p-5 sm:p-8" : "bg-white border border-slate-200 rounded-2xl p-5 sm:p-6"}
            data-testid={`lesson-${currentLesson.id}`}
          >
            <div className={isHero ? "max-w-[68ch] mx-auto" : undefined}>
              <div className="text-[11px] uppercase tracking-widest text-slate-600 font-black mb-2">
                {t("kurs.lesson_n_of", { n: currentLesson.order, course: pick(course.title, lang) })}
              </div>
              <h2 className={isHero
                ? "text-3xl sm:text-4xl font-black text-slate-900 tracking-tight mb-6 leading-tight"
                : "text-2xl sm:text-3xl font-black text-slate-900 tracking-tight mb-4 leading-tight"
              }>{pick(currentLesson.title, lang)}</h2>
            </div>
            {!isHero && pickHero(currentLesson, lang) && (
              <div className="relative rounded-2xl overflow-hidden mb-6 aspect-[16/7] bg-slate-100">
                <img
                  src={pickHero(currentLesson, lang)}
                  alt={pick(currentLesson.hero_alt, lang) || ""}
                  className="w-full h-full object-cover"
                  data-testid="lesson-hero-image"
                />
              </div>
            )}
            <div className="mb-6">
              <Renderer lesson={currentLesson} lang={lang} t={t} />
            </div>
            <div className={isHero ? "max-w-[68ch] mx-auto flex gap-2 pt-4 border-t border-slate-100" : "flex gap-2 pt-4 border-t border-slate-100"}>
              <button
                onClick={() => setPhase("index")}
                className="px-4 py-2.5 rounded-lg border border-slate-300 text-slate-700 text-sm font-semibold hover:bg-slate-50"
                data-testid="lesson-back-to-index"
              >
                <ArrowLeft className="w-4 h-4 inline mr-1" /> {t("kurs.back_to_index")}
              </button>
              <button
                onClick={async () => {
                  await markLessonDone(currentLesson);
                  if (showLessonQuiz) {
                    setPhase("lesson_quiz");
                  } else if (isLast) {
                    if (hasFinalQuiz) {
                      setPhase("final_quiz");
                    } else {
                      setFinished(true);
                      setPhase("finished");
                      load();
                    }
                  } else {
                    setCurrent(current + 1);
                    load();
                  }
                }}
                className="flex-1 py-2.5 rounded-lg bg-slate-900 hover:bg-slate-800 text-sm font-semibold flex items-center justify-center gap-2"
                style={{ color: "#fff" }}
                data-testid="lesson-next"
              >
                {completeLabel} <ArrowRight className="w-4 h-4" />
              </button>
            </div>
          </div>
        );
      })()}

      {phase === "lesson_quiz" && currentLesson && showLessonQuiz && (
        <LessonMiniQuiz
          course={course}
          lesson={currentLesson}
          questions={currentLessonQuestions}
          threshold={course.quiz_pass_threshold || 0.8}
          authHeader={authHeader}
          collectOnly={finalGrading}
          externalBusy={finalBusy}
          onCollect={(lessonAnswers) => {
            const merged = { ...collected };
            lessonAnswers.forEach((a) => { merged[a.question_id] = a.option_index; });
            setCollected(merged);
            const isLast = current === lessons.length - 1;
            if (isLast) { submitFinal(merged); }
            else { setCurrent(current + 1); setPhase("lesson"); }
          }}
          onClose={(passed) => {
            if (passed) {
              const isLast = current === lessons.length - 1;
              if (isLast) {
                setFinished(true);
                setPhase("finished");
              } else {
                setCurrent(current + 1);
                setPhase("lesson");
              }
            } else {
              setPhase("lesson");
            }
            load();
          }}
          t={t}
          lang={lang}
        />
      )}

      {phase === "final_quiz" && hasFinalQuiz && (
        <LessonMiniQuiz
          course={course}
          lesson={{ title: course.title, order: 0 }}
          questions={quiz}
          threshold={course.quiz_pass_threshold || 1.0}
          authHeader={authHeader}
          collectOnly
          externalBusy={finalBusy}
          onCollect={(all) => {
            const map = {};
            all.forEach((a) => { map[a.question_id] = a.option_index; });
            setCollected(map);
            submitFinal(map);
          }}
          onClose={() => setPhase("index")}
          t={t}
          lang={lang}
        />
      )}

      {phase === "final_result" && finalResult && (
        <FinalResultWeb
          course={course}
          result={finalResult}
          lang={lang}
          t={t}
          onRestart={restartCourse}
          onContinue={() => { setFinished(true); setPhase("finished"); load(); }}
        />
      )}

      {phase === "finished" && (
        <CourseFinishedScreen course={course} lang={lang} t={t} />
      )}
    </DoserneShell>
  );
}

/* ============================================================
   FinalResultWeb — aggregate grading result at end of course.
============================================================ */
const FRW = {
  no: { pass: "Gratulerer! Du bestod", fail: "Ikke bestått ennå", score: "Resultat", correctOf: "{c} av {t} riktige", mistakes: "{n} feil", need: "Du må ha 100 % riktig for å bestå. Les gjennom kurset på nytt og prøv igjen.", review: "Dette svarte du feil", your: "Ditt svar", correct: "Riktig svar", restart: "Ta kurset på nytt", cont: "Fortsett" },
  ro: { pass: "Felicitări! Ai promovat", fail: "Încă nu ai promovat", score: "Rezultat", correctOf: "{c} din {t} corecte", mistakes: "{n} greșeli", need: "Ai nevoie de 100% corect ca să promovezi. Reia cursul și încearcă din nou.", review: "Ce ai greșit", your: "Răspunsul tău", correct: "Răspuns corect", restart: "Reia cursul", cont: "Continuă" },
  en: { pass: "Congratulations! You passed", fail: "Not passed yet", score: "Result", correctOf: "{c} of {t} correct", mistakes: "{n} mistakes", need: "You need 100% correct to pass. Retake the course and try again.", review: "What you got wrong", your: "Your answer", correct: "Correct answer", restart: "Retake the course", cont: "Continue" },
};
function FinalResultWeb({ course, result, lang, t, onRestart, onContinue }) {
  const L = FRW[lang] || FRW.no;
  const total = result.total || (result.detail || []).length || 0;
  const correct = result.correct ?? 0;
  const mistakes = Math.max(0, total - correct);
  const scorePct = Math.round((result.score ?? (total ? correct / total : 0)) * 100);
  const passed = !!result.passed;
  const wrong = (result.detail || []).filter((d) => !d.is_correct);
  const fmt = (s, o) => s.replace(/\{(\w+)\}/g, (_m, k) => String(o[k]));

  return (
    <div data-testid="final-result">
      <div className="bg-white border border-slate-200 rounded-2xl p-6 sm:p-8 mb-4 text-center">
        <div className={`w-16 h-16 rounded-full mx-auto flex items-center justify-center ${passed ? "bg-emerald-500" : "bg-rose-500"}`}>
          {passed ? <Trophy className="w-8 h-8 text-white" /> : <RotateCcw className="w-8 h-8 text-white" />}
        </div>
        <div className={`mt-3 text-sm font-black uppercase tracking-wider ${passed ? "text-emerald-600" : "text-rose-600"}`}>{passed ? L.pass : L.fail}</div>
        <div className={`mt-4 rounded-2xl p-5 ${passed ? "bg-emerald-50" : "bg-rose-50"}`}>
          <div className="text-[11px] font-bold uppercase tracking-wider text-slate-500">{L.score}</div>
          <div className={`text-5xl font-black ${passed ? "text-emerald-600" : "text-rose-600"}`} data-testid="final-score">{scorePct}%</div>
          <div className="text-sm text-slate-600 mt-1">{fmt(L.correctOf, { c: correct, t: total })} · {fmt(L.mistakes, { n: mistakes })}</div>
        </div>
        {!passed && <p className="text-sm text-slate-600 mt-4 leading-relaxed">{L.need}</p>}
      </div>

      {!passed && wrong.length > 0 && (
        <div className="mb-4">
          <div className="text-[11px] font-bold uppercase tracking-wider text-slate-500 mb-2">{L.review}</div>
          {wrong.map((d, i) => (
            <div key={i} className="bg-white border border-slate-200 rounded-2xl p-4 mb-3" data-testid={`final-wrong-${i}`}>
              <div className="text-[15px] font-bold text-slate-900 mb-2">{pick(d.question, lang)}</div>
              <div className="flex items-start gap-2 text-sm text-rose-600 font-medium"><XCircle className="w-4 h-4 mt-0.5 shrink-0" /><span>{L.your}: {d.selected_index != null && d.options ? pick(d.options[d.selected_index], lang) : "—"}</span></div>
              <div className="flex items-start gap-2 text-sm text-emerald-600 font-semibold mt-1"><CheckCircle2 className="w-4 h-4 mt-0.5 shrink-0" /><span>{L.correct}: {d.correct_index != null && d.options ? pick(d.options[d.correct_index], lang) : "—"}</span></div>
              {d.explanation && <p className="text-sm text-slate-500 italic mt-2 leading-relaxed">{pick(d.explanation, lang)}</p>}
            </div>
          ))}
        </div>
      )}

      {passed ? (
        <button onClick={onContinue} data-testid="final-continue" className="w-full py-3 rounded-xl bg-cyan-600 hover:bg-cyan-700 text-white text-sm font-semibold flex items-center justify-center gap-2">{L.cont} <ArrowRight className="w-4 h-4" /></button>
      ) : (
        <button onClick={onRestart} data-testid="final-restart" className="w-full py-3 rounded-xl bg-slate-900 hover:bg-slate-800 text-white text-sm font-semibold flex items-center justify-center gap-2"><RotateCcw className="w-4 h-4" /> {L.restart}</button>
      )}
    </div>
  );
}

/* ============================================================
   CourseFinishedScreen — smart end-of-course screen.
   Polls /completion-status and shows the right CTA:
     • passed_signature_required → prominent "Sign to receive certificate"
     • completed + certificate    → "View certificate (PDF)" + acknowledgement
     • fallback                   → generic "back to catalog"
   This is what makes the Welcome course (single lesson, no quiz) route
   the worker to /sign — otherwise LessonIndex's banner is skipped.
============================================================ */
function CourseFinishedScreen({ course, lang, t }) {
  const { authHeader } = useDoserneAuth();
  const [status, setStatus] = useState(null);

  useEffect(() => {
    let alive = true;
    axios.get(`${API}/doserne/kurs/${course.code}/completion-status`, authHeader())
      .then(({ data }) => { if (alive) setStatus(data); })
      .catch(() => { if (alive) setStatus({ state: "unknown" }); });
    return () => { alive = false; };
  }, [course, authHeader]);

  const labels = {
    no: { done: "Bra jobbet!", finishedLine: "Du har fullført", needSign: "Alle leksjoner er bestått!", needSignSub: "Ett siste steg — signer den obligatoriske bekreftelsen for å motta kursbeviset ditt.", signBtn: "Signer og motta kursbeviset", completed: "Kurset er fullført", completedSub: "Kursbeviset ditt er utstedt.", viewCert: "Vis kursbevis (PDF)", viewAck: "Vis signert bekreftelse", backCatalog: "Til kurskatalogen" },
    ro: { done: "Bravo!", finishedLine: "Ai finalizat", needSign: "Toate lecțiile sunt promovate!", needSignSub: "Un ultim pas — semnează confirmarea obligatorie pentru a primi certificatul.", signBtn: "Semnează și primește certificatul", completed: "Curs finalizat", completedSub: "Certificatul tău a fost emis.", viewCert: "Vezi certificatul (PDF)", viewAck: "Vezi confirmarea semnată", backCatalog: "Înapoi la cursuri" },
    en: { done: "Well done!", finishedLine: "You finished", needSign: "All lessons passed!", needSignSub: "One last step — sign the mandatory acknowledgement to receive your certificate.", signBtn: "Sign and receive certificate", completed: "Course completed", completedSub: "Your certificate has been issued.", viewCert: "View certificate (PDF)", viewAck: "View signed acknowledgement", backCatalog: "Back to catalog" },
  };
  const L = labels[lang] || labels.no;

  // Waiting for the completion state — show a light skeleton.
  if (!status) {
    return (
      <div className="bg-white border border-slate-200 rounded-2xl p-8 sm:p-10 text-center" data-testid="course-finished">
        <Loader2 className="w-6 h-6 animate-spin text-slate-400 mx-auto" />
      </div>
    );
  }

  // ---- State 1: needs signature -> BIG orange CTA (this is the fix) ----
  if (status.state === "passed_signature_required") {
    return (
      <div
        className="rounded-3xl bg-gradient-to-br from-slate-900 to-slate-800 text-white p-6 sm:p-8 shadow-xl"
        data-testid="course-finished-sign-required"
      >
        <div className="w-16 h-16 rounded-2xl bg-emerald-500 flex items-center justify-center mx-auto mb-4">
          <CheckCircle2 className="w-8 h-8" />
        </div>
        <div className="text-[11px] uppercase tracking-[0.3em] text-orange-300 font-black text-center mb-2">
          {L.needSign}
        </div>
        <h3 className="text-2xl sm:text-3xl font-black text-center mb-2 leading-tight">
          {pick(course.title, lang)}
        </h3>
        <p className="text-[15px] text-slate-300 text-center max-w-md mx-auto mb-6 leading-relaxed">
          {L.needSignSub}
        </p>
        <Link
          to={`/doserne/kurs/${course.code}/sign`}
          className="flex items-center justify-center gap-2 w-full sm:max-w-sm sm:mx-auto py-3.5 rounded-2xl bg-orange-500 hover:bg-orange-600 active:bg-orange-700 font-black text-white text-sm shadow-lg"
          data-testid="course-finished-sign-btn"
        >
          <PenSquare className="w-5 h-5" /> {L.signBtn}
          <ArrowRight className="w-4 h-4" />
        </Link>
        <Link
          to="/doserne/kurs"
          className="block text-center text-xs text-slate-400 hover:text-white mt-4 underline underline-offset-2"
        >
          {L.backCatalog}
        </Link>
      </div>
    );
  }

  // ---- State 2: fully completed + cert issued ----
  if (status.state === "completed" && status.certificate) {
    return (
      <div className="bg-white border border-emerald-200 rounded-2xl p-6 sm:p-8 text-center" data-testid="course-finished-completed">
        <div className="w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-4 bg-emerald-500" style={{ color: "#fff" }}>
          <Award className="w-8 h-8" />
        </div>
        <div className="text-[11px] uppercase tracking-widest text-emerald-700 font-black mb-1">
          {L.completed}
        </div>
        <h3 className="text-2xl sm:text-3xl font-black text-slate-900 mb-2">{pick(course.title, lang)}</h3>
        <p className="text-[15px] text-slate-600 mb-2">{L.completedSub}</p>
        <div className="text-xs font-mono text-slate-500 mb-6">{status.certificate.cert_number}</div>
        <div className="flex flex-col sm:flex-row gap-2 sm:max-w-md sm:mx-auto">
          <a
            href={`${API}/doserne/certificates/${status.certificate.id}/pdf`}
            target="_blank" rel="noreferrer"
            className="flex-1 inline-flex items-center justify-center gap-2 px-4 py-3 rounded-xl bg-slate-900 text-white text-sm font-black hover:bg-slate-800"
            data-testid="course-finished-view-cert"
          >
            <Award className="w-4 h-4" /> {L.viewCert}
          </a>
          <Link
            to={`/doserne/kurs/${course.code}/sign`}
            className="flex-1 inline-flex items-center justify-center gap-2 px-4 py-3 rounded-xl bg-slate-100 text-slate-900 text-sm font-black hover:bg-slate-200"
          >
            <FileSignature className="w-4 h-4" /> {L.viewAck}
          </Link>
        </div>
        <Link
          to="/doserne/kurs"
          className="block text-center text-xs text-slate-400 hover:text-slate-700 mt-4 underline underline-offset-2"
        >
          {L.backCatalog}
        </Link>
      </div>
    );
  }

  // ---- Fallback: generic finished screen (shouldn't happen but safe) ----
  return (
    <div className="bg-white border border-slate-200 rounded-2xl p-6 sm:p-8 text-center" data-testid="course-finished">
      <div className="w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-4 bg-emerald-500" style={{ color: "#fff" }}>
        <CheckCircle2 className="w-8 h-8" />
      </div>
      <h3 className="text-2xl sm:text-3xl font-black text-slate-900 mb-2">{L.done}</h3>
      <p className="text-[15px] text-slate-600 mb-6">
        {L.finishedLine} <strong>{pick(course.title, lang)}</strong>.
      </p>
      <Link
        to="/doserne/kurs"
        className="inline-flex items-center gap-1.5 px-5 py-2.5 rounded-lg bg-slate-900 text-sm font-semibold"
        style={{ color: "#fff" }}
        data-testid="finished-back-to-catalog"
      >
        {L.backCatalog} <ArrowRight className="w-4 h-4" />
      </Link>
    </div>
  );
}
